import { FC, MouseEvent } from 'react';
import classNames from 'classnames';

import { useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { useSelectedStore, useShoppingMode } from '@/hooks';

import { IconSvg } from '../IconSvg';
import { Button } from '../Button';
import { addressAsString, ILocalAddress } from '../molecules/address';

import styles from './CartShoppingInfo.module.scss';

const CartShoppingInfo: FC = () => {
  const { onlyDelivery, onlyPickup, deliveryAndPickup } = useShoppingMode();
  const { store } = useSelectedStore();
  const {
    delivery_details,
    delivery_address,
    setState: setEcomState,
  } = useEcomStoreSelector(['delivery_details', 'delivery_address']);
  const { setState: setUIState } = useUiStoreSelector([]);
  const shoppingMethod = delivery_details.pickup ? 'Pickup' : 'Delivery';
  const address = delivery_details.pickup
    ? store?.full_address
    : addressAsString(delivery_address as ILocalAddress);

  const goToStoreSelection = (event: MouseEvent) => {
    event.stopPropagation();

    const shouldUsePickup =
      onlyPickup || (deliveryAndPickup && delivery_details.delivery);
    const temporaryFulfillmentMethod = shouldUsePickup ? 'PICKUP' : 'DELIVERY';

    setEcomState({
      temporary_fulfillment_method: temporaryFulfillmentMethod,
    });
    setUIState({ is_cart_open: false, is_store_location_open: true });
  };

  return store ? (
    <Button
      aria-label={`Change ${shoppingMethod.toLowerCase()} location: ${address}`}
      className={styles.store_locator__button}
      onClick={goToStoreSelection}
      size="md"
      type="button"
    >
      <div className={styles.store_locator__button_content}>
        {!onlyDelivery && (
          <figure
            className={classNames(styles.shopping_mode_indicator, {
              [styles.selected]: delivery_details.pickup,
            })}
          >
            <IconSvg name="store" />
          </figure>
        )}
        {!onlyPickup && (
          <figure
            className={classNames(styles.shopping_mode_indicator, {
              [styles.selected]: delivery_details.delivery,
            })}
          >
            <IconSvg name="car" />
          </figure>
        )}
        <div className={styles.shopping_info}>
          <p>
            <span className={styles.shopping_info__title}>
              {`${shoppingMethod} at`}
            </span>{' '}
            &nbsp;
            {delivery_details.pickup && (
              <span className={styles.shopping_info__address}>
                {store.name}
              </span>
            )}
          </p>
          <p className={styles.shopping_info__address} title={address}>
            {address}
          </p>
        </div>
      </div>
    </Button>
  ) : (
    <Button className={styles.select_store_button} onClick={goToStoreSelection}>
      Select your store
    </Button>
  );
};

export { CartShoppingInfo };
